import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    debounce,
    FormControlLabel,
    Switch,
    TextField,
} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import { useAppDispatch, useAppSelector, AppDispatch } from "../../framework/customStore";
import {
    TransportOrderListItemDto,
    TransportOrderState,
    transportOrderStates,
    getTransportOrderSaveDto,
} from "../../models/transport/transportOrder";
import {
    setUndeliveredOrders,
    setTodaysOrders,
    setKeyword,
    setVehicle,
    setTimeRange,
    toggleOrderStateSelected,
    setSenderCustomer,
    setOrderType,
    setBillingCustomer,
} from "../../store/transport/transportOrdersSlice";
import { MuiDateRangePicker } from "../framework/muiDatepicker";
import { MainLayout } from "../layout/mainLayout";
import { showError } from "../storage/storageUtils";
import { CustomerSelect } from "./components/customerSelect";
import { fetchEmployees } from "../../store/employeeSlice";
import {
    addTransportOrder,
    fetchTransportOrders,
    editTransportOrderWithNotification
} from "../../services/transportOrderService";
import { VehicleSelect } from "./components/vehicleSelect";
import { OrderTypeSelect } from "./components/orderTypeSelect";
import { showSuccessMessage } from "../../models/store/storeActions";
import { showApiError } from "../framework/formUtils";
import { OrdersTable } from "./transportOrder/ordersTable";
import { ICustomerListItem } from "../../models/customer/customerListItem";
import { getCustomerList } from "../../services/customerService";
import { getVehicles } from "../../services/vehicleService";
import { VehicleListItemDto } from "../../models/transport/vehicle";
import { OwnerParameters } from "../../models/owner/ownerParameters";
import { customHistory } from "../../framework/customHistory";
import { useInterval } from "usehooks-ts";
import { Base } from "../../framework/base";
import { PagedResult } from "../../models/pagedResult/pagedResult";
import { LoadingIndicator } from "../framework/loadingIndicatorNew";


const REFETCH_INTERVAL = 1000 * 60;

const debouncedSetKeyword = debounce((dispatch: AppDispatch, value: string) => {
    dispatch(setKeyword(value));
}, 1000);

export const TransportOrdersMain = () => {
    const ownerParameters = new OwnerParameters();
    const hasTransportOrdersEnabled = ownerParameters.getOwnerHasTransportEnabled();

    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const state = useAppSelector((state) => state.transportOrders);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [orderItems, setOrderItems] = useState<TransportOrderListItemDto[]>([]);
    const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);

    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const isEditingRef = useRef(false);

    const [customers, setCustomers] = useState<ICustomerListItem[]>();
    const [vehicles, setVehicles] = useState<VehicleListItemDto[]>();
    const [searchKeyword, setSearchKeyword] = useState<string>("");

    const employees = useAppSelector((state) => state.employees.employees);
    const employeeId = useAppSelector((state) => state.user.employeeId);
    const ordersDataChanged = useAppSelector(
        (state) => state.transportOrders.ordersDataChanged
    );

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [totalCount, setTotalCount] = useState(0);

    if (!hasTransportOrdersEnabled) customHistory.push("/");

    const handleSubmit = async(values: TransportOrderListItemDto) => {
        return new Promise<TransportOrderListItemDto>((resolve, reject) => {
            const payload = getTransportOrderSaveDto(values);
            const isNew = values?.id === null;

            payload.createdEmployeeId ??= employeeId;
            payload.modifiedEmployeeId = isNew ? null : employeeId;

            const afterSave = async(id: string, successMsg: string) => {
                const orders = await fetchOrders();
                const found = orders?.items.some(order => order.id === id);
                if (found) {
                    setHighlightedRowId(id);
                    setTimeout(() => setHighlightedRowId(null), 2000);
                    dispatch(showSuccessMessage(successMsg));
                } else {
                    dispatch(showSuccessMessage(t("transport.order.orderNotVisibleWithCurrentFilters")));
                }
            };

            if (isNew) {
                addTransportOrder(payload)
                    .then(async(res) => {
                        await afterSave(res?.data?.id, t("transport.order.orderSavedSuccessfully"));
                        resolve(res.data);
                    })
                    .catch(() => {
                        reject(new Error(t("transport.order.orderSaveFailed")));
                    });
            } else {
                editTransportOrderWithNotification(payload, values.id)
                    .then(async(res) => {
                        await afterSave(values.id, t("transport.order.orderEditedSuccessfully"));
                        resolve(res.data);
                    })
                    .catch(() => {
                        reject(new Error(t("transport.order.orderEditFailed")));
                    });
            }
        });
    };

    useEffect(() => {
        setSearchKeyword(state.keyword);
    }, []);

    const handleOrderFetchfail = (error: unknown) => showError(error);

    const fetchOrders = async(): Promise<PagedResult<TransportOrderListItemDto>> => {
        setIsLoading(true);

        try {
            const result = await fetchTransportOrders({
                ...state,
                page: currentPage,
                pageSize: pageSize
            });

            // Use ref which updates immediately to check if we are in edit mode
            if (!isEditingRef.current) {
                setOrderItems(result?.items);
                setTotalCount(result?.totalCount);
            }

            return result;
        } catch (error) {
            showError(
                t("transport.order.fetchFailed")
            );

            return {
                items: [],
                currentPage: 1,
                pageSize: 50,
                totalCount: 0,
                totalPages: 0,
                hasPreviousPage: false,
                hasNextPage: false
            };
        } finally {
            setIsLoading(false); // Prevent loading state from persisting
        }
    };

    // Update the ref immediately when edit mode changes
    useEffect(() => {
        isEditingRef.current = isEditMode;
    }, [isEditMode]);

    useInterval(() => {
        if (!isEditMode) {
            fetchOrders().catch((err) => {
                console.error("Error during scheduled fetch:", err);
            });
        }
    }, REFETCH_INTERVAL);

    useEffect(() => {
        if (!isEditMode) {
            const fetchOrdersAsync = async() => await fetchOrders();
            fetchOrdersAsync()
                .catch(handleOrderFetchfail);
        }
    }, [
        state.timeRange,
        state.createdRange,
        state.senderCustomer,
        state.receiverCustomer,
        state.selectedOrderStates,
        state.billingCustomerId,
        state.keyword,
        state.vehicleId,
        state.orderType,
        ordersDataChanged,
        isEditMode
    ]);

    useEffect(() => {
        if (employees.length === 0) {
            dispatch(fetchEmployees() as any);
        }
    }, [employees]);

    const refreshCustomers = useCallback(() => {
        getCustomerList()
            .then((res) =>
                setCustomers(
                    res?.data.sort((a, b) => (a.name > b.name ? 1 : -1))
                )
            )
            .catch(showApiError);
    }, []);

    useEffect(() => {
        refreshCustomers();
    }, [refreshCustomers]);

    useEffect(() => {
        getVehicles()
            .then((res) => setVehicles(res?.data))
            .catch(showApiError);
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [
        state.timeRange,
        state.senderCustomer,
        state.receiverCustomer,
        state.selectedOrderStates,
        state.billingCustomerId,
        state.keyword,
        state.vehicleId,
        state.orderType,
    ]);

    useEffect(() => {
        if (!isEditMode) {
            fetchOrders().catch(handleOrderFetchfail);
        }
    }, [currentPage, pageSize]);

    const toggleState = (toggledState: TransportOrderState) => {
        dispatch(toggleOrderStateSelected(toggledState));
    };

    if (!customers || !vehicles) {
        return <LoadingIndicator tooltip={t("common.loading")} />;
    }

    return (
        <MainLayout
            noContainer
            topComponent={
                <div>
                    <Grid2 container spacing={2}>
                        <Grid2>
                            <MuiDateRangePicker
                                labels={[
                                    t("common.timeperiodStarts"),
                                    t("common.timeperiodEnds"),
                                ]}
                                small
                                value={state.timeRange}
                                disabled={isEditMode}
                                onChange={(val) => {
                                    let [start, end] = val;

                                    if (!!start || !!end) {
                                        start ??= Base.dateAddMonths(
                                            end,
                                            -3
                                        ).toDate();
                                        end ??= Base.dateAddMonths(
                                            start,
                                            3
                                        ).toDate();
                                    }

                                    dispatch(setTimeRange([start, end]));
                                }}
                            />
                        </Grid2>
                        <Grid2>
                            <VehicleSelect
                                small
                                label={t("vehicle.vehicle")}
                                value={state.vehicleId}
                                disabled={isEditMode}
                                onChange={(val: string) =>
                                    dispatch(setVehicle(val))
                                }
                            />
                        </Grid2>
                        <Grid2>
                            <CustomerSelect
                                small
                                label={t("transport.order.sender")}
                                value={state.senderCustomer}
                                disabled={isEditMode}
                                onChange={(val: string) =>
                                    dispatch(setSenderCustomer(val))
                                }
                            />
                        </Grid2>
                        <Grid2>
                            <CustomerSelect
                                small
                                label={t("transport.order.billingCustomer")}
                                value={state.billingCustomerId}
                                disabled={isEditMode}
                                onChange={(val: string) =>
                                    dispatch(setBillingCustomer(val))
                                }
                            />
                        </Grid2>
                        <Grid2>
                            <OrderTypeSelect
                                small
                                label={t("common.type")}
                                value={state.orderType}
                                disabled={isEditMode}
                                onChange={(val: string) =>
                                    dispatch(setOrderType(val))
                                }
                            />
                        </Grid2>
                        <Grid2>
                            <TextField
                                size="small"
                                label={t("common.search")}
                                value={searchKeyword}
                                disabled={isEditMode}
                                onChange={(e) => {
                                    setSearchKeyword(e.currentTarget.value);
                                    debouncedSetKeyword(dispatch, e.currentTarget.value);
                                }}
                            />
                        </Grid2>
                        <Grid2
                            container
                            direction="row"
                            justifyContent="flex-end"
                            alignItems="center"
                        >
                            <Grid2 container>
                                {transportOrderStates.map((tos) => (
                                    <FormControlLabel
                                        key={tos.value}
                                        control={
                                            <Switch
                                                checked={state.selectedOrderStates[tos.value]}
                                                onChange={() => toggleState(tos.value)}
                                                color={tos.colorName}
                                                disabled={isEditMode}
                                            />
                                        }
                                        label={t(tos.name)}
                                        sx={{
                                            "& .MuiFormControlLabel-label": {
                                                color: "primary.dark",
                                                fontWeight: "bold",
                                                fontSize: "0.8rem"
                                            }
                                        }}
                                    />
                                ))}
                            </Grid2>
                        </Grid2>
                        <Grid2
                            container
                            spacing={1}
                            direction="row"
                            justifyContent="flex-end"
                            alignItems="center"
                            sx={{ marginLeft: "auto" }}
                        >
                            <Grid2>
                                <Button
                                    variant="contained"
                                    disabled={isEditMode}
                                    size="small"
                                    onClick={() => {
                                        setSearchKeyword("");
                                        dispatch(setTodaysOrders());
                                    }}
                                >
                                    {t("transport.order.todaysOrders")}
                                </Button>
                            </Grid2>
                            <Grid2>
                                <Button
                                    variant="contained"
                                    disabled={isEditMode}
                                    size="small"
                                    onClick={() => {
                                        setSearchKeyword("");
                                        dispatch(setUndeliveredOrders());
                                    }}
                                >
                                    {t("transport.order.undeliveredOrders")}
                                </Button>
                            </Grid2>
                        </Grid2>
                        {isLoading && (
                            <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                                <LoadingIndicator
                                    tooltip={t("transport.order.loadingOrders")}
                                    loading={true}
                                />
                            </Box>
                        )}
                    </Grid2>
                </div>
            }
        >
            <OrdersTable
                orders={orderItems}
                setOrders={setOrderItems}
                fetchOrders={fetchOrders}
                highlightedRowId={highlightedRowId}
                customers={customers}
                refreshCustomers={refreshCustomers}
                vehicles={vehicles}
                handleSubmit={handleSubmit}
                setIsEditMode={setIsEditMode}
                setHighlightedRowId={setHighlightedRowId}
                currentPage={currentPage}
                pageSize={pageSize}
                totalCount={totalCount}
                onPageChange={setCurrentPage}
                onPageSizeChange={setPageSize}
            />
        </MainLayout>
    );
};
